<template lang="pug">
div
  .flex.items-center
    .flex-1.text-xl.font-sans.font-semibold Filters
    a.text-xs.text-gray-750.hover_underline.hover_text-black(v-if="userSelectedOptions.length" href="#" @click.prevent="onReset") Reset
  ul
    li.pt-8(v-for="filter in filterOptions")
      .flex.cursor-pointer(@click="toggleFilterFold(filter)")
        .flex-1.font-sans.font-semibold.mb-2 {{ filter.name }}
        VArrow(:direction="isFilterOpen(filter) ? 'top' : 'bottom'")

      template(v-if="filter.filter_type === filterTypes.SELECTION")
        ul.filter-list(:class="isFilterOpen(filter) ? 'filter-open' : 'filter-closed'")
          li.flex.items-center.py-1(v-for="option in filter.options")
            input.mr-3(
              type="checkbox"
              @change="onFilterChange"
              :name="filter.endpoint_filter"
              :id="option.filter"
              :checked="option.selected"
              :value="option.filter"
            )
            label.m-0.cursor-pointer.w-full(:for="option.filter") {{ option.name }}

        .cursor-pointer(@click="toggleFilterFold(filter)")
          template(v-if="!isFilterOpen(filter)" v-for="option in filter.options")
            span.text-xs.collapsed-options(v-if="option.selected") {{ option.name }}
          template(v-if="!isFilterOpen(filter) && !filter.options.filter(option => option.selected).length")
            span.text-xs.text-gray-850 All

      template(v-else-if="filter.filter_type === filterTypes.RANGE")
        VDeprecatedRangeSlider(
          @update="e => setRange({ ...e, ...filter })"
          v-show="isFilterOpen(filter)"
          :margin="1"
          :ticks="rangeTicks(filter).ticks"
          :labels="rangeTicks(filter).labels"
          :start="startRange(filter)"
        )
        .text-xs.text-gray-850.cursor-pointer(v-if="!isFilterOpen(filter)" @click="toggleFilterFold(filter)") {{ textRange(filter) }}
</template>

<script lang="ts" setup>
import { filterTypes } from '@/composables/useFilters'
import type { FilterOption, RangeFilter } from '@/entities/filter'

const props = defineProps({
  options: {
    type: Array,
    required: false,
    default: () => [],
  },
  userSelectedOptions: {
    type: Array,
    required: false,
    default: () => [],
  },
})
const emit = defineEmits(['set-select-option', 'set-range', 'reset'])
const openedFilters = ref<string[]>([])
const filterOptions = computed(() => props.options)

function onFilterChange(e: Event) {
  emit('set-select-option', {
    filter: (e.target as HTMLInputElement).name,
    option: (e.target as HTMLInputElement).value,
    value: (e.target as HTMLInputElement).checked,
  })
}

function rangeTicks({ ticks, multiplier }: RangeFilter) {
  return {
    ticks,
    labels: ticks.map(tick => (tick > 0 ? tick * multiplier : tick)).map(xlMoney),
  }
}

function startRange({ value, ...filter }: RangeFilter) {
  const { ticks } = rangeTicks(filter)
  return value ? [ticks.indexOf(value[0]), ticks.indexOf(value[1])] : [0, ticks.length - 1]
}

function textRange({ value, ...filter }: RangeFilter) {
  const { ticks, labels } = rangeTicks(filter)
  const range = !value ? startRange(filter) : [ticks.indexOf(value[0]), ticks.indexOf(value[1])]
  const low = labels[range[0]]
  const high = labels[range[1]]
  const isDefaultRange = range[0] === 0 && range[1] === ticks.length - 1

  return isDefaultRange ? 'All' : `${low} - ${high}`
}

// may be better in useFilters
function setRange(value: RangeFilter & { min: number; max: number }) {
  emit('set-range', {
    endpoint: [value.endpoint_filter_min, value.endpoint_filter_max],
    value: [value.min, value.max],
  })
}

// Shorten very large price value to abbreviated string
function xlMoney(num: number) {
  const len = num.toString().length
  if (len < 10) {
    return '$' + num / 1000000 + 'M'
  } else if (len > 9) {
    return '$' + num / 1000000000 + 'B'
  } else {
    return '$' + num
  }
}

function toggleFilterFold(filter: FilterOption) {
  const opened = openedFilters.value.indexOf(filter.name)
  openedFilters.value = opened > -1 ? [] : [filter.name]
}

function onReset() {
  openedFilters.value = []
  emit('reset')
}

function isFilterOpen(filter: FilterOption) {
  return openedFilters.value.indexOf(filter.name) > -1
}
</script>

<style lang="stylus" scoped>
.collapsed-options
  &:after
    content ', '
  &:last-child:after
    content ''

.filter-list
  overflow hidden
  transition max-height 0.2s ease-in-out

.filter-open
  // this max-height needs to be adjusted if contents of filter is larger.
  max-height 100vh
  transition max-height 0.2s ease-in-out

.filter-closed
  max-height 0rem

[type="checkbox"]:focus
  box-shadow none
</style>
